// Keyframe interpolation with easing functions

// Easing functions
export const easings = {
  linear: (t) => t,
  easeIn: (t) => t * t,
  easeOut: (t) => t * (2 - t),
  easeInOut: (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
  easeInCubic: (t) => t * t * t,
  easeOutCubic: (t) => (--t) * t * t + 1,
  easeInOutCubic: (t) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
  bounce: (t) => {
    const n1 = 7.5625, d1 = 2.75;
    if (t < 1 / d1) return n1 * t * t;
    if (t < 2 / d1) return n1 * (t -= 1.5 / d1) * t + 0.75;
    if (t < 2.5 / d1) return n1 * (t -= 2.25 / d1) * t + 0.9375;
    return n1 * (t -= 2.625 / d1) * t + 0.984375;
  },
  elastic: (t) => t === 0 || t === 1 ? t : -Math.pow(2, 10 * t - 10) * Math.sin((t * 10 - 10.75) * ((2 * Math.PI) / 3)),
};

export function lerp(a, b, t) {
  return a + (b - a) * t;
}

// Standard props + any custom numeric props (leftArmAngle, rightArmAngle, etc.)
const STANDARD_PROPS = ['x', 'y', 'rotation', 'scaleX', 'scaleY', 'opacity'];
const SKIP_KEYS = ['frame'];

export function getInterpolatedProps(keyframes, frame, easing = 'easeInOut') {
  if (!keyframes || keyframes.length === 0) return {};

  const sorted = [...keyframes].sort((a, b) => a.frame - b.frame);

  if (frame <= sorted[0].frame) return extractAllNumericProps(sorted[0]);
  if (frame >= sorted[sorted.length - 1].frame) return extractAllNumericProps(sorted[sorted.length - 1]);

  let kf1 = sorted[0];
  let kf2 = sorted[sorted.length - 1];

  for (let i = 0; i < sorted.length - 1; i++) {
    if (frame >= sorted[i].frame && frame <= sorted[i + 1].frame) {
      kf1 = sorted[i];
      kf2 = sorted[i + 1];
      break;
    }
  }

  if (kf1.frame === kf2.frame) return extractAllNumericProps(kf1);

  const rawT = (frame - kf1.frame) / (kf2.frame - kf1.frame);
  const easeFn = easings[easing] || easings.easeInOut;
  const t = easeFn(rawT);

  const props = {};
  // Collect all numeric keys from both keyframes
  const allKeys = new Set([...Object.keys(kf1), ...Object.keys(kf2)]);

  for (const prop of allKeys) {
    if (SKIP_KEYS.includes(prop)) continue;
    if (typeof kf1[prop] === 'number' && typeof kf2[prop] === 'number') {
      props[prop] = lerp(kf1[prop], kf2[prop], t);
    } else if (typeof kf1[prop] === 'number') {
      props[prop] = kf1[prop];
    } else if (typeof kf2[prop] === 'number') {
      props[prop] = kf2[prop];
    }
  }

  return props;
}

function extractAllNumericProps(kf) {
  const props = {};
  for (const [key, val] of Object.entries(kf)) {
    if (SKIP_KEYS.includes(key)) continue;
    if (typeof val === 'number') props[key] = val;
  }
  return props;
}

export function getObjectPropsAtFrame(obj, frame) {
  const easing = obj.easing || 'easeInOut';
  const interpolated = getInterpolatedProps(obj.keyframes, frame, easing);
  return {
    x: interpolated.x ?? obj.x,
    y: interpolated.y ?? obj.y,
    rotation: interpolated.rotation ?? obj.rotation ?? 0,
    scaleX: interpolated.scaleX ?? obj.scaleX ?? 1,
    scaleY: interpolated.scaleY ?? obj.scaleY ?? 1,
    opacity: interpolated.opacity ?? obj.opacity ?? 1,
    // Pass through all custom props (limb angles, etc.)
    leftArmAngle: interpolated.leftArmAngle ?? 0,
    rightArmAngle: interpolated.rightArmAngle ?? 0,
    leftLegAngle: interpolated.leftLegAngle ?? 0,
    rightLegAngle: interpolated.rightLegAngle ?? 0,
  };
}
